import React from "react";

import {
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
  CircularProgress,
  // MUI's Tooltip, NOT `@repo/ui`'s — that package's own Tooltip is a 0-byte
  // stub that exports nothing at runtime (see ProductEditPage for the same
  // documented reason). Importing the real one here means every consumer of
  // the shared Button gets a working `disabledReason` tooltip for free.
  Tooltip as MuiTooltip,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { fieldFill } from "../../../theme/fieldChrome";
import { getContrastColor } from "../../../utils/parsers";

// const StyledMuiButton = styled(MuiButton)<MuiButtonProps>(({ theme, variant, destructive, size }) => {
interface ButtonProps extends Omit<MuiButtonProps, "size" | "variant" | "sx"> {
  variant?: "primary" | "secondary" | "tertiary" | "text";
  size?: "x-small" | "small" | "medium" | "large";
  /**
   * LINK props. MUI's `Button` already renders an `<a>` when `href` is present and
   * `props` is spread through verbatim below — so these have always WORKED at
   * runtime; they were simply not TYPED, because `MuiButtonProps` defaults to the
   * `"button"` component and so carries none of the anchor attributes.
   *
   * The gap is not cosmetic: it is what makes "this button opens an external page"
   * unreachable without an `as any` at the call site, which is the sideways
   * workaround working-conventions §47 exists to prevent (improve the shared
   * component, don't route around it). Declared here rather than fixed per call
   * site so every app gets a correctly-typed link button.
   *
   * `rel` is deliberately not defaulted — a call site opening a new tab must state
   * `rel="noopener noreferrer"` itself, and a default would hide its absence.
   */
  href?: string;
  target?: string;
  rel?: string;
  /** `true` = always red (loud confirm / transient menus). `"hover"` = neutral
   *  at rest, red on hover + focus — for persistent chrome triggers (e.g. a
   *  header "Delete") so the red doesn't distract while idle. */
  destructive?: boolean | "hover";
  highlight?: boolean;
  loading?: boolean;
  sx?: any;
  /**
   * Why this button is disabled, e.g. "Make a change to save". When set AND
   * the button is `disabled`, the button explains itself instead of
   * swallowing the interaction silently (working-conventions §46 already
   * requires this of a disabled MENU row; this is the same principle for a
   * Button).
   *
   * Mechanism: a `disabled` + `disabledReason` button does NOT get the
   * native `disabled` attribute — a truly disabled element fires no pointer
   * or focus events at all, which is exactly the "click does nothing" bug
   * being fixed. Instead it renders `aria-disabled="true"` (announces as
   * disabled to assistive tech, per WAI-ARIA), stays focus-reachable, and
   * its own `onClick` is intercepted so the click has no side effect other
   * than surfacing the reason. Hover AND focus open an MUI Tooltip; a click
   * also force-opens it (covers touch / no-hover devices and browsers, e.g.
   * Safari on macOS, that don't focus a `<button>` on click).
   *
   * With no `disabledReason`, a disabled button is unchanged from before —
   * still the native `disabled` attribute, `pointer-events: none`, no
   * tooltip machinery at all.
   */
  disabledReason?: string;
  /**
   * THE SURFACE THIS BUTTON SITS ON, when it is not the page/card default.
   *
   * A statement of SITUATION, not of appearance — the call site says where the
   * button is, the component decides what that costs it. That split is the
   * whole point: a call site that instead said "make the disabled fill lilac"
   * would be re-specifying a design decision this file owns, and would drift
   * from it the moment the disabled family is re-tuned (it has been twice:
   * VERA-291 → VRG-365 → VRG-407).
   *
   * WHY IT EXISTS. The disabled primary's fill is deliberately a step of the
   * SURFACE (VRG-365 replaced an opacity dim with an explicit mid-tone fill for
   * exactly this reason — opacity "doesn't move the fill toward the surface").
   * But "the surface" was hardcoded to the neutral ramp, which is correct on a
   * white/near-black card and wrong the moment the button sits on a tinted one:
   * on Home's brand-tinted reward callout the grey chip stopped reading as part
   * of the callout and read as a dead foreign element dropped on top of it.
   *
   * `"brand-tint"` = a brand-tinted callout (the house tint — `alpha(brand[600],
   * 0.12)`, the same one `@repo/ui`'s outlined Alert and the §34 selectable-card
   * hover use). Today it re-points ONLY the DISABLED `primary` tokens, because
   * that is the only combination that exists: the enabled primary is a solid
   * near-black chip that reads correctly on any surface, and no secondary /
   * tertiary / text button currently sits on a tint. Said out loud rather than
   * left implicit — a reader should not have to discover by experiment that
   * `surface` does nothing on a `secondary`.
   *
   * NOT a context. `@repo/ui` has exactly one context today (`ColorModeContext`
   * in VeraThemeProvider) and no surface/tone provider, so reading the tint
   * ambiently would mean INVENTING a second mechanism for one call site. A prop
   * is also the more honest model: a tinted callout is a local band a few
   * elements wide, not an ambient region, and a provider would have to be
   * re-declared around every such band anyway.
   */
  surface?: "default" | "brand-tint";
  /**
   * RENDER AS A DIFFERENT ELEMENT — in practice react-router's `Link`, so a button
   * whose job is to NAVIGATE is still a real anchor: right-clickable, middle-clickable,
   * cmd-clickable, and readable in the browser's status bar. An `onClick` that calls
   * `navigate()` looks identical and silently loses all four.
   *
   * WHY THIS HAD TO BE DECLARED. It already worked at RUNTIME — `component` reaches
   * `MuiButton` through the rest-spread below and MUI does the right thing with it — but
   * it was never in this interface, so every call site that used it was a type ERROR.
   * `apps/platform`'s `RecentActivity.tsx:73` carried exactly that error
   * (`TS2322 … 'component' does not exist`) in the baseline, and
   * `BusinessFiguresPage.tsx` records the workaround in a comment: *"`@repo/ui`'s Button
   * is also not polymorphic"*, so it reached for a plain `Link` instead. Two call sites
   * had independently hit the same wall — which is the §36 signal to fix the shared
   * component rather than route around it a third time (§47: the direction is DOWN).
   *
   * WHY NOT A GENERIC. MUI's own `Button` is an `OverridableComponent`, so its
   * `component` prop is only expressible through a generic CALL SIGNATURE. This wrapper
   * is a `forwardRef` with a fixed props type and cannot carry one without changing the
   * exported shape for all ~200 call sites. The cast at the render site is confined to
   * these two props and is documented there.
   */
  component?: React.ElementType;
  /**
   * react-router's `to`, meaningful only alongside `component={RouterLink}`.
   *
   * Typed as a bare `string` on purpose: `@repo/ui` is layer 1 and has no react-router
   * dependency, and inventing one so a single prop could be typed as `To` would couple
   * the shared library to this app's router for no gain. Every call site in both apps
   * passes a `ROUTES.*` string.
   */
  to?: string;
}

const StyledMuiButton = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const {
    size,
    variant = "primary",
    destructive,
    disabled = false,
    disableElevation = true,
    highlight = false,
    loading = false,
    sx,
    onClick,
    children,
    disabledReason,
    surface = "default",
    // Pulled OUT of the rest-spread deliberately — see `polymorphic` at the render
    // site for why these two cannot go through it.
    component,
    to,
    ...muiRest
  } = props;

  const theme = useTheme();

  // Only the DISABLED (not merely loading) case gets an explanation — a
  // spinner already communicates "wait", it doesn't need a "why" tooltip.
  const explainDisabled = disabled && !loading && Boolean(disabledReason);

  const [explainOpen, setExplainOpen] = React.useState(false);
  const explainTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  React.useEffect(() => () => {
    if (explainTimer.current) clearTimeout(explainTimer.current);
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (explainDisabled) {
      // A native `disabled` button never reaches this handler at all — that
      // silence is the bug. Intercept instead: swallow the click's effect
      // but surface the reason, and keep it open longer than a hover would
      // (2.6s) since a click is a deliberate "why can't I" moment.
      event.preventDefault();
      event.stopPropagation();
      setExplainOpen(true);
      if (explainTimer.current) clearTimeout(explainTimer.current);
      explainTimer.current = setTimeout(() => setExplainOpen(false), 2600);
      return;
    }
    if (disabled || loading) return;
    onClick?.(event);
  };

  // `destructive` is either true (always red — loud confirm / transient menus)
  // or "hover" (neutral at rest, red on hover/focus — persistent chrome triggers).
  const destructiveAlways = destructive === true;
  const destructiveHover = destructive === "hover";
  const redOnHover = destructiveAlways || destructiveHover;
  const focusVisibleRed = destructiveHover
    ? {
        color: theme.palette.error.main,
        background: alpha(theme.palette.error.main, 0.05),
        borderColor: alpha(theme.palette.error.main, 0.3),
      }
    : {};

  const spinnerSize =
    size === "small" || size === "x-small"
      ? 18
      : 20;

  /**
   * THE TENANT'S ACCENT — the fill, hover and ink of `variant="primary"`.
   *
   * `primary[950]` is the darkest step of a NEUTRAL GREY ramp, and it is
   * `#000000` in both apps. So the shared primary button was hardcoded black on
   * every tenant, and `apps/app` shipped TWO visibly different primary buttons:
   * this one always black, and MUI's `variant="contained"` — restyled in
   * `apps/app/src/settings/mui/MuiButton.tsx` — in the brand's own colour. Which
   * one a screen got depended only on which component its author reached for.
   *
   * The gate is `palette.brand.isTenantDerived`, the SAME signal `CountBadge`
   * and `LogoLoader` already branch on (`apps/app/src/settings/mui/brandRamp.ts`
   * names it). It is not a new concept and not a new palette key — it is true
   * only when `brandRamp()` successfully parsed a real Studio `primary_color`:
   *
   *   apps/platform       `brand` is the static Vera ramp and has no such key
   *                       -> false -> `primary[950]`, byte-identical to before
   *   apps/app, unthemed  `brandRamp(null)` -> false -> black, as today
   *   apps/app, tenant    -> true  -> the brand's own colour
   *
   * so `apps/platform` cannot move: nothing there ever sets the flag.
   *
   * `brand[600]` IS the tenant colour verbatim — `brandRamp`'s step 600 returns
   * the parsed base unmixed — which is the same value `MuiButton.tsx` paints
   * from `primary.main`. The two primary buttons finally agree.
   *
   * INK IS COMPUTED, NOT WHITE. A brand may pick a pale accent, and
   * `white[100]` on pale yellow is unreadable. `getContrastColor` is the same
   * luma pick `MuiButton.tsx` uses for `contained`, so the ink agrees too. It
   * assumes `#rrggbb`, which `brandRamp` always emits (and this branch only
   * runs when its hex parse succeeded), so it cannot be handed a bad value here.
   */
  const brandPalette = theme.palette.brand as typeof theme.palette.brand & {
    isTenantDerived?: boolean;
  };
  const tenantAccent = brandPalette?.isTenantDerived ? brandPalette[600] : null;
  const primaryFill =
    tenantAccent || theme.palette.primary[950] || theme.palette.primary.main;
  /**
   * Hover walks the tenant's OWN ramp one step darker.
   *
   * The neutral path keeps `primary[800]` (#454545), which against a #000000
   * fill is a LIGHTENING — the right move for black, and the wrong one for a
   * saturated colour, where lifting toward mid-grey desaturates rather than
   * deepens. `brand[700]` is the tenant colour mixed toward black, so every
   * brand gets the conventional darken-on-hover instead.
   */
  const primaryHover = tenantAccent
    ? brandPalette[700]
    : theme.palette.primary[800];
  const primaryInk = tenantAccent
    ? getContrastColor(tenantAccent)
    : theme.palette.white[100];

  const spinnerColor = destructiveAlways
    ? variant === "primary"
      ? theme.palette.common?.white || "#ffffff" // white spinner on the red fill
      : theme.palette.error.main
    : variant === "primary"
      ? primaryInk
      : theme.palette.primary[950];

  // The one "disabled ink" colour shared by every OUTLINED/borderless variant
  // (secondary, tertiary, text). Chosen from the theme's own `primary` ramp —
  // which is already mode-aware by construction (dark mode remaps it onto the
  // zinc scale, see theme.ts) — rather than MUI's `text.disabled`
  // (rgba(0,0,0,.38) / #52525b): measured, `text.disabled` on this app's own
  // dark `fieldFill` (#1f1f23) lands at 2.12:1, too close to vanishing.
  // `primary[400]` measures ~3.4:1 against `fieldFill` in BOTH modes (#888888
  // on #fafafa light, #71717a on #1f1f23 dark) — clearly muted, still legible.
  // The border/fill are left UNCHANGED for these variants (not dimmed) so the
  // control keeps its shape; only the label recedes. Opacity dimmed the whole
  // control before, which is what made a disabled secondary nearly disappear.
  //
  // ⚠️ REACH (measured VRG-407, in the browser): this value — and the primary's
  // `disabledPrimaryInk` below — only reaches a button on the `disabledReason`
  // path. Emotion folds `sx` into ONE class, and MUI's own
  // `&.Mui-disabled { color: action.disabled }` lives in that same class with an
  // extra `.Mui-disabled`, so for a NATIVELY disabled button it is two classes
  // against one and MUI always wins: the label computes to rgba(0,0,0,.26) in
  // light / rgba(255,255,255,.3) in dark, ~1.8–2.7:1, for every variant. A
  // `disabledReason` button withholds the native attribute (see the prop doc),
  // `.Mui-disabled` is never applied, and only then do these tokens render.
  // Raising the specificity here would make ~every disabled button in both apps
  // darker than it is today, so it is deliberately NOT done as a side effect of
  // a colour tweak — but do not read these numbers as "what a disabled button
  // looks like". Measure the button you mean.
  const disabledInk = theme.palette.primary[400];

  const getButtonStyles = (theme: any) => {
    const baseStyle = {
      borderRadius: 2,
      // `large` is 49, not 48, to match the Input's rendered height exactly. A
      // button placed beside a field is the whole point of this size, and at 48
      // every such pair in the platform sat one pixel short — invisible on its
      // own, but it meant no field/button row could ever be truly flush and each
      // call site either lived with it or hand-patched a height. Changed here so
      // the pairing works everywhere instead of per-site.
      height:
        size == "large" ? 49 : size == "small" ? 32 : size == "x-small" ? 28 : 40,
      padding: 2,
      paddingX: 4,
      fontSize: 16,
      textTransform: "none" as const,
      transition: "all 0.2s ease-in",
      minWidth: "fit-content",
      cursor: disabled ? "not-allowed" : "pointer",
      // No hover box-shadow while disabled — this base rule used to be
      // unconditional, which mattered less when a disabled button always
      // carried the native `disabled` attribute (`pointer-events: none`
      // means `:hover` never matches). It matters now: the
      // `disabled` + `disabledReason` path below deliberately keeps pointer
      // events ON so a hover can open the explanation tooltip, and without
      // this guard the button would ALSO grow a hover shadow, undercutting
      // "clearly inert".
      ...(disabled ? {} : { "&:hover": { boxShadow: `0px 5px 8px -2px rgba(0, 0, 0, 0.05)` } }),
      "& .MuiButton-startIcon": {
        "&:hover": {
          backgroundColor: "transparent ",
        },
      },
    };

    if (variant == "tertiary") {
      return {
        ...baseStyle,
        // TERTIARY IS THE BORDERLESS VARIANT. Its border matches its own background on purpose,
        // so the shape is carried by fill alone — that is what keeps it from competing with the
        // bordered `secondary` buttons in a page's top bar.
        //
        // It briefly carried primary[100] (the table's border) while the table toolbar was still
        // tertiary. That was the wrong lever: giving tertiary an outline made it a quieter
        // secondary rather than a distinct variant, and collapsed the difference between the two.
        // The toolbar is `secondary` now, and secondary owns the subtle border, so tertiary goes
        // back to having none.
        //
        // LIGHT AND DARK NOW AGREE, on ONE token rather than a per-mode fork. Light used to be
        // `white[100]` — #ffffff, which on the white card a tertiary almost always sits on
        // measured 1.000:1. Not "subtle": absent. Since the variant carries its shape by fill
        // alone, a fill that matches the surface leaves it with no rest affordance at all, so a
        // light tertiary was indistinguishable from `text` while the dark one had a real one.
        // That is a mode fork in the LADDER itself, not just in a value.
        //
        // `primary[50]` is already mode-aware by construction (the neutral ramp is remapped onto
        // zinc in dark, theme.ts) so the one token gives each mode the same PRESENCE rather than
        // the same literal — measured in the browser, both land on the same step above their own
        // paper: light #f6f6f6 on #ffffff → 1.081:1, dark #18181b on #0f0f12 → 1.080:1. It is
        // also the step `fieldFill` already argues for as the quiet-but-legible fill.
        //
        // It stays UNDER secondary despite being a hair stronger than secondary's own #fafafa
        // fill (1.044:1), because a secondary's presence is really its border — a 1.243:1 edge,
        // the loudest thing about it — and tertiary has no edge to add to its flat fill. The
        // ladder is intact: solid fill → subtle border → subtle fill → nothing.
        //
        // Known limit, inherited from `fieldFill` and stated rather than hidden: against the
        // #f4f5f5 PAGE this is ~1.007:1, i.e. invisible. Tertiary sits on cards, modals and
        // drawers; one placed directly on the page background is still shapeless at rest.
        border: `1px solid ${theme.palette.primary[50] || theme.palette.gray[100]}`,
        background: theme.palette.primary[50] || theme.palette.gray[100],
        opacity: 1,
        color: disabled
          ? disabledInk
          : destructiveAlways
            ? theme.palette.error.main
            : theme.palette.text.primary,
        ...(disabled
          ? {}
          : {
              "&:hover": {
                // One step up the same ramp, which reads as a step BECAUSE the rest state is now
                // on the ramp too: measured against the rest fill, 1.144:1 light / 1.189:1 dark —
                // a bigger move than rest makes against the card, so hover is the louder of the
                // two. It is also what indicates keyboard focus: `:focus-visible` resolves to
                // this same fill (nothing in either app draws an outline ring), so the step has
                // to stay perceptible for reasons beyond hover.
                background: redOnHover
                  ? alpha(theme.palette.error.main, 0.05)
                  : theme.palette.primary[100] || theme.palette.gray[200],
                // Tracks the hover fill for the same reason the rest border tracks the rest fill.
                // It was pinned at primary[50], which is now exactly the REST fill and would ring
                // a hovered button with a lighter 1px halo — an edge on the variant defined by
                // having none. The destructive rim is deliberately NOT matched: there the border
                // is the danger signal and is meant to out-weigh its own 5% fill.
                border: `1px solid ${redOnHover ? alpha(theme.palette.error.main, 0.3) : theme.palette.primary[100] || theme.palette.gray[200]}`,
                color: redOnHover ? theme.palette.error.main : theme.palette.text.primary,
              },
            }),
        "&:focus-visible": disabled ? {} : focusVisibleRed,
      };
    }
    if (variant == "secondary") {
      return {
        ...baseStyle,
        // `primary[100]`, the token the data table and every field outline themselves with, so a
        // row of secondary buttons sitting above a table reads as one family with it. Was
        // primary[200] — a step darker than the table beneath it, which made the toolbar heavier
        // than the content it acts on (measured 1.53:1 on white against the table's 1.24:1).
        border: `1px solid ${destructiveAlways && !disabled ? alpha(theme.palette.error.main, 0.3) : theme.palette.primary[100] || theme.palette.gray[300]}`,
        // `fieldFill` — THE SAME SURFACE AS AN INPUT. A secondary button and the search box beside
        // it in a table toolbar sit at the same elevation and should be the same colour; they were
        // not (#1f1f23 button against a #18181b field in dark, white button against a #fafafa
        // field in light), which read as the search box being a different colour from the controls
        // around it. Sharing the token means the row cannot drift apart again.
        //
        // Dark's value is unchanged (#1f1f23): a fill that recedes toward the card rather than the
        // more prominent #27272a, so the button still reads as a quiet recessed surface.
        background: fieldFill(theme),
        opacity: 1,
        color: disabled
          ? disabledInk
          : destructiveAlways
            ? theme.palette.error.main
            : theme.palette.text.primary,
        ...(disabled
          ? {}
          : {
              "&:hover": {
                background: redOnHover
                  ? alpha(theme.palette.error.main, 0.05)
                  : theme.palette.primary[50] || theme.palette.gray[100],
                border: `1px solid ${redOnHover ? alpha(theme.palette.error.main, 0.3) : theme.palette.primary[200] || theme.palette.gray[300]}`,
                color: redOnHover ? theme.palette.error.main : theme.palette.text.primary,
              },
            }),
        "&:focus-visible": disabled ? {} : focusVisibleRed,
      };
    }
    if (variant == "primary") {
      // DISABLED PRIMARY (VRG-365 — supersedes the VERA-291 "stay visibly
      // black, only opacity-dim" approach below).
      //
      // VERA-291 kept the disabled fill at the full enabled colour (black in
      // light, and — because `primary[950]` flips to white under dark mode's
      // neutral-ramp remap, see theme.ts — WHITE in dark) and dimmed it with
      // `opacity: 0.85`. That is fine in light mode (black at 85% opacity
      // over a light page is still very dark) but breaks in dark mode for
      // the opposite reason: a white fill at 85% opacity over a near-black
      // card measured `#dddede` — still almost white, so the disabled
      // primary stayed the BRIGHTEST thing on screen, more prominent than
      // every enabled control around it.
      //
      // Opacity was the wrong instrument either way — it doesn't move the
      // fill toward the surface, it just lets the surface show through a
      // thin veil of the SAME colour. Fixed here by picking an explicit
      // mid-tone fill instead: `primary[400]`, which is already mode-aware
      // by construction (light `#888888`, dark `#71717a` — see the
      // `darkNeutral` remap in theme.ts) and reads as a genuinely receded
      // grey chip in both modes, not a washed-out version of the CTA.
      //
      // Per-mode, because one grey cannot be right on both. `primary[400]`
      // (#888888) is a MID grey: on a dark page it reads as a muted raised fill,
      // but on white it is dark enough to look like a live button rendered in
      // the wrong colour rather than an inert one. Light steps up two stops to
      // `primary[200]` (#d1d1d1) — clearly filled, clearly not actionable.
      const isLight = theme.palette.mode === "light";
      // VERA-304 amends the DARK fill only: `primary[300]` (#52525b), not
      // `primary[400]` (#71717a). VRG-407 (everything else in this block) was
      // measured against the LABEL and left the dark fill alone; looked at on a
      // dark modal, #71717a is the brightest thing in the footer — the inert
      // control out-shouts the enabled Cancel beside it, which inverts what
      // disabled communicates. Light is untouched: VRG-407's #d1d1d1 reasoning
      // holds and was never the complaint.
      //
      // VERA-304 adds the `surface="brand-tint"` fork below. Everything above
      // describes the DEFAULT surface and is untouched by it.
      const onBrandTint = surface === "brand-tint";
      const disabledBg = onBrandTint
        ? // ALPHA, not a mixed literal — and that is the load-bearing choice.
          // A translucent brand fill composites against whatever is actually
          // behind it, so ONE value is correct on the light card (over the
          // band's own 12% it lands on rgb(217,201,246)) and on the dark one
          // (rgb(48,32,79)) with no mode split. A literal would have needed
          // two, and would have been wrong the moment a band used a different
          // alpha.
          //
          // 0.20 against the band's 0.12: enough separation that the button is
          // a discernible shape rather than a label floating in the band, small
          // enough that it still reads as the band's own material. Measured
          // against the band it sits on, this fill alone is only 1.30:1 (light)
          // / 1.20:1 (dark) — almost nothing, which is why the rim below is
          // doing the real work of drawing the chip (1.64:1 / 1.45:1).
          //
          // BOTH ARE UNDER WCAG 1.4.11's 3:1 for a UI-component boundary, and
          // that is allowed here for one specific reason: 1.4.11 exempts
          // INACTIVE components. This file's `earned` banner border, by
          // contrast, does answer to 3:1 — it distinguishes two live states.
          // The moment this button is enabled it becomes the solid
          // near-black/white primary fill at >15:1 against the band, so the
          // ACTIVE state is never relying on the exemption. If a future variant
          // of this treatment is ever used on an ENABLED control, these numbers
          // have to go up; do not copy them across on the assumption they pass.
          alpha(theme.palette.brand[600], 0.2)
        : isLight
          ? /*
             * VRG-720: `primary[100]` (#e7e7e7), was `primary[200]` (#d1d1d1).
             *
             * VRG-407 picked #d1d1d1 to be "clearly filled, clearly not
             * actionable", and that reasoning was sound against the alternative
             * it was weighed against (`primary[400]` #888888, which on white
             * looks like a live button in the wrong colour). What it was never
             * weighed against is the state it actually shipped in: its label
             * never rendered (see the `&&.Mui-disabled` note below), so what was
             * on screen was a mid-grey slab carrying text at 1.80:1. A filled
             * shape with no readable label does not read as "not actionable",
             * it reads as unfinished — which is how it was reported.
             *
             * With the label fixed the fill is the only thing left overstating
             * the control: on a #fafafa card #d1d1d1 is the darkest element in
             * the card after the title. #e7e7e7 still reads as filled — it is a
             * step, not a disappearance — while receding instead of competing.
             *
             * ⚠️ KNOWN COST, stated rather than buried: `primary[500]` #6d6d6d
             * on #e7e7e7 measures 4.18:1, where VRG-407's stated target was the
             * ~3.4:1 the outlined family carries (#888888 on white is 3.54:1),
             * so the label is marginally LOUDER against its own fill than the
             * family standard. The ramp has no step between #888888 (2.87:1
             * here, under 3) and #6d6d6d, so holding 3.4 exactly would mean a
             * new token. Taken deliberately: ratio-against-fill is not
             * perceived prominence, and the control as a whole is quieter on
             * the page than it was. If a disabled-label token is ever added,
             * this is the site to revisit.
             *
             * Dark is untouched. VERA-304 moved it to `primary[300]` (#52525b)
             * for the opposite problem — on a dark modal the disabled fill was
             * the BRIGHTEST thing in the footer — and nothing here changes that.
             */
            theme.palette.primary[100]
          : theme.palette.primary[300];
      // VRG-407 — the LABEL on that fill. Was `text.primary`, on a comment
      // claiming it measured 5.28:1 (light) / 4.63:1 (dark) and cleared AA.
      // The light figure was never measured: `text.primary` in light is MUI's
      // default `rgba(0, 0, 0, 0.87)` (this theme overrides `text` only in
      // DARK, see theme.ts), which composites over the #d1d1d1 fill to #1b1b1b
      // and measures 11.28:1 — full body-text weight. That is not "clears AA
      // comfortably", it is a disabled control whose label is as heavy as an
      // enabled one, which is exactly how it read on screen.
      //
      // The target is the weight the OUTLINED variants already have. Their
      // `disabledInk` is documented at ~3.4:1 against their fill, and matching
      // it here makes the whole disabled family read at one muted weight
      // instead of the primary being the loudest disabled thing on the page.
      //
      // Mode-flipped for the same reason `disabledBg` is: the neutral ramp is
      // remapped onto zinc in dark (theme.ts `darkNeutral`), so a step DOWN the
      // ramp is darker in light and lighter in dark. Both land on the target
      // against their own fill — measured in the browser, not derived:
      //   light  primary[500] #6d6d6d on #d1d1d1 → 3.39:1
      //   dark   primary[600] #d4d4d8 on #71717a → 3.27:1
      // (`primary[400]`, i.e. reusing `disabledInk` verbatim, is only 2.32:1 on
      // light's darker disabled fill — too faint. Same token, two fills, so the
      // index has to differ to hold the ratio.)
      // Dark ink steps down WITH the fill, to hold VRG-407's own target rather
      // than drift off it. That target is ~3.4:1 — the weight the outlined
      // variants already have — so the disabled family reads at ONE muted
      // weight. On the darker fill, keeping `primary[600]` (#d4d4d8) would
      // measure 5.25:1, i.e. louder than the enabled-adjacent look VRG-407 was
      // deliberately avoiding. `primary[500]` (#a1a1aa) on #52525b measures
      // 3.03:1 — the nearest step to target on the new fill.
      // Both modes land on index 500 — but for different reasons and against
      // different fills, so this is a coincidence of the ramp, not one value:
      // light #6d6d6d on #d1d1d1 → 3.39:1, dark #a1a1aa on #52525b → 3.03:1.
      // Written flat rather than as `isLight ? 500 : 500`, which reads as a
      // mistake; if either fill moves again, re-measure and split it back out.
      //
      // On a brand tint the ink stays in the CALLOUT's family — the state to
      // communicate is "not yet", not "broken", and a neutral grey label on a
      // lilac band says the latter. Mode-split because the fill it sits on is
      // pale in light and deep in dark, so the ramp has to be walked in
      // opposite directions; the brand ramp itself is NOT remapped in dark
      // (theme.ts only remaps the neutral ramps), so these are the same two
      // literals in both modes and only the surface under them moves.
      //
      // MEASURED against the composited fill above, not chosen by eye:
      //   light  brand[600] #7F48DF on rgb(217,201,246) → 3.50:1
      //   dark   brand[500] #8F68EA on rgb(48,32,79)    → 3.70:1
      // which is the ~3.4:1 band this file already targets for every other
      // disabled variant (see `disabledInk`, and VRG-407's note on the default
      // primary). Deliberately BELOW AA's 4.5:1 for text: WCAG 1.4.3 exempts
      // inactive components, and VRG-407 rejected an 11.28:1 disabled label
      // precisely because a disabled control reading at full body weight looks
      // enabled. `brand[800]` (5.62:1 light) is the step to take if that call
      // is ever reversed — the tokens are one index apart, not a redesign.
      //
      // One caveat worth stating: a SATURATED ink at 3.5:1 reads a little more
      // present than a neutral one at the same ratio, because hue carries
      // salience luminance contrast does not measure. That is the reason this
      // button drops the crisp saturated outline the `dev` variant has — a
      // brand label inside a brand rim is exactly the enabled `dev` look, and
      // the soft rim below is what keeps the two apart.
      const disabledPrimaryInk = onBrandTint
        ? isLight
          ? theme.palette.brand[600]
          : theme.palette.brand[500]
        : theme.palette.primary[500];
      return {
        ...baseStyle,
        // The disabled border reuses the fill token, as it always has. On the
        // tint that token is translucent and `background-clip` defaults to
        // `border-box`, so the fill paints UNDER the border and the rim renders
        // as the same colour composited twice — 1-(0.8²) = 0.36 effective,
        // rgb(199,175,241) light / rgb(64,40,108) dark. A soft deeper edge:
        // 1.26:1 / 1.20:1 against the fill it outlines, 1.64:1 / 1.45:1 against
        // the band it separates the chip from. Kept rather than flattened to
        // `transparent`, because the fill alone is only 1.30:1 / 1.20:1 against
        // the band and the chip would have no discernible edge at all — but
        // deliberately NOT given a hard saturated outline, which is exactly the
        // enabled `dev` variant's look.
        border: `1px solid ${disabled ? disabledBg : destructiveAlways ? theme.palette.error.main : primaryFill}`,
        background: disabled
          ? disabledBg
          : destructiveAlways
            ? theme.palette.error.main
            : primaryFill,
        opacity: 1,
        color: disabled
          ? disabledPrimaryInk
          : destructiveAlways
            ? theme.palette.common?.white || "#ffffff" // red bg always needs white text
            : primaryInk,
        // No hover response while disabled — the mid-grey fill is the only
        // signal that the button is inert, and a hover reaction would
        // cancel it.
        ...(disabled
          ? {}
          : {
              "&:hover": {
                background: destructiveAlways
                  ? alpha(theme.palette.error.main, 0.8)
                  : primaryHover,
                border: `1px solid ${destructiveAlways ? alpha(theme.palette.error.main, 0.3) : primaryHover}`,
              },
            }),
      };
    }
    if (variant == "text") {
      return {
        ...baseStyle,
        color: disabled
          ? disabledInk
          : destructiveAlways
            ? theme.palette.error.main
            : theme.palette.text.primary,
        ...(disabled
          ? {}
          : {
              "&:hover": {
                background: redOnHover
                  ? alpha(theme.palette.error.main, 0.05)
                  : theme.palette.primary[50] || theme.palette.gray[100],
                color: redOnHover ? theme.palette.error.main : theme.palette.text.primary,
              },
            }),
        "&:focus-visible":
          disabled || !destructiveHover
            ? {}
            : { color: theme.palette.error.main, background: alpha(theme.palette.error.main, 0.05) },
      };
    }
    if (variant == "dev") {
      return {
        ...baseStyle,
        color: theme.palette.brand[500],
        border: `1px solid ${theme.palette.brand[500]}`,
        backgroundColor: alpha(theme.palette.brand[500], 0.1),
        "&:hover": {
          backgroundColor: alpha(theme.palette.brand[500], 0.2),
        },
      };
    }
    return baseStyle;
  };

  const buttonStyles = getButtonStyles(theme);
  const finalStyles = highlight && !disabled
    ? {
        ...buttonStyles,
        "&:hover": {
          ...buttonStyles["&:hover"],
          backgroundColor: alpha(theme.palette.brand[500], 0.1),
          borderColor: theme.palette.brand[500],
          color: theme.palette.brand[800],
        },
      }
    : disabled
      ? {
          ...buttonStyles,
          // Belt-and-braces: force hover/focus back to the resting look.
          // Each variant above already omits its OWN hover override while
          // disabled, but MuiButton renders with no `variant` prop forwarded
          // (defaults to "text"), which carries its own built-in hover
          // overlay from `action.hover` — invisible before because a native
          // `disabled` button's `pointer-events: none` blocked `:hover` from
          // ever matching. The `disabledReason` path below deliberately
          // keeps pointer events on, so this pins hover/focus to identical
          // background/border/color no matter what MUI would otherwise add.
          "&:hover": {
            backgroundColor: (buttonStyles as any).background ?? (buttonStyles as any).backgroundColor,
            border: (buttonStyles as any).border,
            color: (buttonStyles as any).color,
            boxShadow: "none",
          },
          "&:focus-visible": {
            backgroundColor: (buttonStyles as any).background ?? (buttonStyles as any).backgroundColor,
            border: (buttonStyles as any).border,
            color: (buttonStyles as any).color,
          },
          /*
           * ── THE DISABLED LABEL ACTUALLY REACHING THE BUTTON (VRG-720) ────────
           *
           * Every variant above computes a deliberate disabled ink — `disabledInk`
           * (`primary[400]`) for the outlined family, `disabledPrimaryInk`
           * (`primary[500]`) for the filled primary — and until now NONE of them
           * rendered on a natively disabled button. `disabledInk`'s own docblock
           * measured and predicted this: emotion folds `sx` into ONE class, and
           * MUI's `&.Mui-disabled { color: action.disabled }` sits in its own class
           * WITH an extra `.Mui-disabled`, so it is two classes against one and MUI
           * always wins. Confirmed in the browser before changing anything: the
           * app's disabled "Access certificate" computed `rgba(0, 0, 0, 0.26)`,
           * which composites over the `#d1d1d1` fill to `#9b9b9b` — 1.80:1, against
           * the 3.39:1 the ink was chosen for. A disabled control whose label cannot
           * be read is not a quiet control, it is an unlabelled slab.
           *
           * `&&` doubles the emotion class reference, giving (0,3,0) against MUI's
           * (0,2,0). Specificity, not `!important`: a caller's own `sx` or a
           * `disabledReason` override must still be able to win, and `!important`
           * would take that away.
           *
           * It reads `buttonStyles.color` rather than naming a token, so each
           * variant keeps the ink IT chose and this cannot drift away from them —
           * the four branches above remain the single source for that decision.
           *
           * ⚠️ This is the change `disabledInk` deliberately did NOT make: "Raising
           * the specificity here would make ~every disabled button in both apps
           * darker than it is today, so it is deliberately NOT done as a side
           * effect of a colour tweak." It is no longer a side effect — it is the
           * point, requested for both apps, and the numbers the file already
           * documents (light `#6d6d6d` on `#d1d1d1` → 3.39:1, dark `#a1a1aa` on
           * `#52525b` → 3.03:1) now describe what renders instead of what was
           * intended. Those comments stop being aspirational.
           */
          "&&.Mui-disabled": {
            color: (buttonStyles as any).color,
          },
        }
      : buttonStyles;

  // The `component` / `to` pair, applied as ONE cast object rather than left in
  // `muiRest`. `MuiButton` is an `OverridableComponent`: the only signature that accepts
  // `component` is generic over it (`<C extends ElementType>(props: { component: C } & …)`),
  // and a spread whose static type merely SAYS `component?: React.ElementType` cannot
  // drive that inference — so putting these two back in the rest-spread would move the
  // type error from the call site into this file rather than removing it.
  //
  // The cast is deliberately scoped to exactly these two keys, and the object is empty
  // when `component` is absent, so the ~200 ordinary `<Button>` call sites keep full
  // prop checking through `muiRest` and nothing extra reaches the DOM.
  const polymorphic: any = component ? { component, to } : {};

  // TODO: something very odd with SELECT achorEl that doesn allow styled to be in the CompoundComponentContext. https://stackoverflow.com/questions/71659578/how-to-use-anchorel-with-styled-in-mui/71663031
  // Omit custom props so they don't reach the DOM
  const button = (
    <MuiButton
      {...polymorphic}
      // See the `forwardRef` note on the export at the bottom of this file. The
      // ref lands on MuiButton in BOTH branches below, including the one wrapped
      // in a Tooltip, because it is this same element that the Tooltip clones.
      ref={ref}
      disableElevation={disableElevation}
      disableTouchRipple={true}
      disableRipple={explainDisabled}
      onClick={handleClick}
      // Native `disabled` for every disabled button EXCEPT the
      // `disabledReason` case, which needs real pointer/focus events to
      // reach `handleClick` — see the prop doc above.
      disabled={explainDisabled ? false : disabled || loading}
      aria-disabled={explainDisabled ? true : undefined}
      onMouseEnter={explainDisabled ? () => setExplainOpen(true) : undefined}
      onMouseLeave={explainDisabled ? () => setExplainOpen(false) : undefined}
      onFocus={explainDisabled ? () => setExplainOpen(true) : undefined}
      onBlur={explainDisabled ? () => setExplainOpen(false) : undefined}
      sx={{ ...finalStyles, ...sx }}
      {...muiRest}
    >
      {loading && (
        <CircularProgress
          size={spinnerSize}
          thickness={5}
          sx={{ color: spinnerColor, mr: children ? 1 : 0 }}
        />
      )}
      {children}
    </MuiButton>
  );

  if (!explainDisabled) return button;

  return (
    // `describeChild` — MUI defaults this to `false`, which makes Tooltip set
    // `aria-label={title}` on the trigger instead of `aria-describedby`.
    // `aria-label` REPLACES the accessible name entirely, so a screen reader
    // would announce "Make a change to save" and never "Save" at all.
    // `describeChild` keeps `aria-describedby` (name + description: "Save …
    // Make a change to save"), which is what a disabled-with-explanation
    // control should announce. Trade-off: MUI also mirrors the tooltip text
    // into the native `title` attribute while closed, so a browser's own
    // (slower, plain) tooltip could theoretically race with ours on hover —
    // accepted, since MUI's opens well before a native title tooltip would.
    <MuiTooltip title={disabledReason} open={explainOpen} placement="top" describeChild>
      {button}
    </MuiTooltip>
  );
});
StyledMuiButton.displayName = "StyledMuiButton";

/**
 * REF-FORWARDING, and it was not — which broke a feature outright.
 *
 * Both this export and `StyledMuiButton` above were plain function components,
 * so a `ref` passed to the shared Button was silently dropped and React logged
 * "Function components cannot be given refs" on every render. `MuiButton` itself
 * forwards to its root `<button>`, so the capability was there; only these two
 * wrappers stood in the way.
 *
 * What it cost, measured 2026-08-23: `apps/app`'s certificates toolbar keeps a
 * `sortButtonRef` to anchor its multi-sort popover, and
 * `useCertificatesPageState.ts:266` opens that popover with
 * `setSortConfigAnchor(sortButtonRef.current)`. The ref never attached, so that
 * expression was always `null`; the popover's `open={Boolean(sortConfigAnchor)}`
 * was therefore always `false`, and clicking Sort on DESKTOP with a sort already
 * applied did **nothing at all** — the whole configure surface was unreachable
 * there. Note it did not merely mis-position: it never opened, which is why the
 * React warning was the only symptom anyone could see.
 *
 * `apps/platform` was unaffected: its `Sort.tsx` hangs its ref on a MUI
 * `ButtonGroup`, which forwards. That asymmetry is exactly what §47 predicts —
 * a gap in the shared base shows up in whichever app happens to lean on it.
 *
 * Additive for every existing call site: a component that passes no ref is
 * unchanged.
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => (
  <StyledMuiButton {...props} ref={ref}>
    {props.children}
  </StyledMuiButton>
));
Button.displayName = "Button";

export default Button;
